import React from "react";

import Logo from '../Logo'
import Footer from '../Footer'

class RationalExpressionsEquations extends React.Component {

    render() {
        return (
            <div>

                <Logo />
                <div className='bodyDiv'>
                    <h2 style={{ paddingBottom: "25px" }}>Equations With Rational Expressions</h2>
                </div>

                <div className="aboutBodyDiv">

                    <p className="p-body" style={{ paddingTop: "25px" }}>
                        In this lesson, we're gonna combine what we know about solving equations, simplifying rational expressions, and a few new steps to learn how to solve 
                        equations with rational expressions.
                    </p>

                    <p className="p-body-small">
                        Just a heads up, these equations you are about to see might look scary, but they're not as tough as they look.  The key thing is to remember our 
                        steps for inverse operations.  Remember that stuff?  To separate two terms, we do the opposite of what is happening in the equation.  These 
                        problems can sometimes get messy, with a lot of distribution and combining like terms, but most of the time, there should be a way to cancel out 
                        a lot of terms.  You'll see what I mean in a second.  
                    </p>

                    <p className="p-body-medium">
                        We've seen what simplifying rational expressions looks like; now let's see what solving an equation containing rational expressions might look 
                        like.  Just a heads up, this might look a little bit messy, but really just focus on the part where I'm simplifying the equation down.  From there, 
                        I'm just going to use the quadratic formula to solve the equation.
                    </p>

                    <img 
                        src={"https://i.ibb.co/v4kfTpy/Rational-Expression-Equation1.png"}
                        alt="Adding rational expressions problem."
                        width={500}
                        height={900}
                        style={{ width: "30%", minWidth: "250px", height: "auto"}}
                    />

                    <p className="p-body-medium" style={{ paddingTop: "25px" }}>
                        Now let's see an example where we can see some of that cancelling out to make things easier.  You'll see that in this scenario, I actually didn't 
                        need to use the quadratic formula.  This is just because, just by chance, I didn't end up with a quadratic; instead, I just ended up with a regular 
                        linear equation that I can solve like I solve any one variable equation.    
                    </p>

                    <img 
                        src={"https://i.ibb.co/zfXbk01/Rational-Expression-Equation2.png"}
                        alt="Adding rational expressions problem."
                        width={500}
                        height={500}
                        style={{ width: "30%", minWidth: "250px", height: "auto"}}
                    />

                    <p className="p-body-medium" style={{ paddingTop: "25px" }}>
                        The important take away here is to be patient and flexible.  When solving an equation with rational expressions, you might need to pull out your 
                        factoring skills, your quadratic formula skills, or just solving one variable equations.
                    </p>

                    <p className="p-body-medium">
                        <strong>Regardless of whatever equation you end up with, </strong> it should always come down to working with a quadratic or a linear equation.  
                        Now you might look at the first picture above, and see that wasn't a quadratic, but there should always be some work around to break down what 
                        you ended up with.  I don't know if I can promise you this, but it really should most of the time end up being working with a quadratic or a linear 
                        equation.
                    </p>

                    <p className="p-body-small">
                        By the way, apologies for the horrible handwriting.  If you have any clarification questions, don't hesitate to ask.
                    </p>

                    <h4 className="h4-blue-headers" style={{ paddingTop: "75px" }}>Try it!</h4>

                    <p className="p-body">
                        1.  Solve the following equation: &nbsp;
                        <div className="frac" style={{ paddingRight: "10px" }}>
                            <span className="top">5</span>
                            <span className="symbol">/</span>
                            <span className="bottom">x</span>
                        </div>
                        -
                        <div className="frac" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
                            <span className="top">5</span>
                            <span className="symbol">/</span>
                            <span className="bottom">6</span>
                        </div>
                        = 5
                    </p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>A)  7/6</p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>B)  6/7</p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>C)  -7/6</p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>D)  -6/7</p>

                    <p className="p-body">
                        2.  Solve the following equation: &nbsp;
                        <div className="frac" style={{ paddingRight: "10px" }}>
                            <span className="top">6</span>
                            <span className="symbol">/</span>
                            <span className="bottom">x - 3</span>
                        </div>
                        + 9 = 
                        <div className="frac" style={{ paddingLeft: "10px" }}>
                            <span className="top">-3</span>
                            <span className="symbol">/</span>
                            <span className="bottom">x - 3</span>
                        </div>
                    </p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>A)  x = 2</p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>B)  x = -2</p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>C)  x = 3</p>
                    <p className="p-body-small" style={{ marginLeft: "4%" }}>D)  x = 9</p>

                    <h4 className="h4-blue-headers" style={{ paddingTop: "75px" }}>Create it!</h4>

                    <p className="p-body">
                        This lesson is probably one of the harder ones for you.  Before proceeding, please don't hesitate to reach out with any questions.  Just click the 
                        "Contact Us" link at the bottom right of this page.
                    </p>

                    <p className="p-body-small">
                        Your task this time is going to be completing a challenge problem.  Try to document all your steps as best as you can.  First, try to solve this 
                        problem completely on your own.  If you get stuck, then try reading the steps that they're showing.  If your completely stuck, which is completely 
                        fine, you can also reach out to us.  We can get on a call and crack this together.
                    </p>

                    <p className="p-body-small">
                        The problem is Example 6 from <a href={"https://www.chilimath.com/lessons/advanced-algebra/solving-rational-equations/"} target={"_blank"} 
                        className="anchor">this page</a>.  Once you've found Example 6, be sure not to read the explanation and steps they give below.  The only way 
                        you can master this is by giving it your own shot first.  It's completely fine, and it's actually really good, to try this on your own and struggle 
                        a bit with it first; then go for help.
                    </p>

                </div>

            <Footer />
            </div>
        )
    }
}

export default RationalExpressionsEquations